// @flow

import { useMemo } from 'react';
import moment from 'moment';
import type { Step2Props } from './types';
import {
  useGrossRentCertExistsForYear,
  useHudFloorPlansWithPricing,
} from './hooks';
import { getChangedFloorPlanRows } from './utils';

export const useStep2 = ({
  open,
  reactHookFormProps,
  isEditMode,
}: Step2Props) => {
  const [effectiveDate, floorPlanChanges] = reactHookFormProps.watch([
    'effectiveDate',
    'floorPlanChanges',
  ]);

  const today = useMemo(() => moment().startOf('day').toDate(), []);
  const priorValuesEffectiveDate = isEditMode ? effectiveDate : today;
  const { floorPlans, isLoading } = useHudFloorPlansWithPricing(
    open,
    priorValuesEffectiveDate,
  );
  // The "certifications already exist this year" warning is an add-mode concept;
  // editing an existing change never surfaces it.
  const { exists: grossRentCertExistsForYear } = useGrossRentCertExistsForYear(
    effectiveDate,
    open && !isEditMode,
  );

  // Both modes: every floor plan with a positive "New" value. In edit mode the
  // form is prefilled with the change's saved values, so this shows the FULL
  // resulting gross rent change (all floor plans in it, edited or not, plus
  // additions) — not just the fields touched this session.
  const changedRows = useMemo(
    () => getChangedFloorPlanRows(floorPlans, floorPlanChanges ?? {}),
    [floorPlanChanges, floorPlans],
  );

  return {
    changedRows,
    effectiveDate,
    grossRentCertExistsForYear,
    isLoading,
  };
};
